import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { addCurrentParticipantAssignment, assignAndCompleteForMe, getItemMissingQuantity, resolveCheckboxIntent, toggleMyCompletion, } from "@/lib/planAssign";
import { CompletionChoiceSheet } from "./CompletionChoiceSheet";
/**
 * Shared checkbox behavior for every place an item can be checked off (section
 * rows, the detail modal, the "שלי" tab, collections). It keeps a single rule
 * set so the meaning of a tap is identical everywhere:
 *
 * - assigned to me → toggle only my own completion (never the whole item, never
 *   anyone else's part).
 * - assigned to others / nobody → open an explicit choice sheet instead of
 *   silently assigning + completing.
 *
 * Returns the tap handler plus the choice-sheet element to render. Callers spread
 * patches through their own `onUpdate`, so this works for both the section view
 * and the per-row detail callbacks.
 */
export function useCheckboxAction(item, myParticipantId, myDisplayName, participants, onUpdate) {
    const [choice, setChoice] = useState(null);
    const apply = useCallback((patch) => {
        // Skip empty patches (e.g. take-responsibility no-ops) so we don't bump
        // the plan version for nothing.
        if (Object.keys(patch).length > 0)
            onUpdate(patch);
    }, [onUpdate]);
    const onCheckboxTap = useCallback(() => {
        const intent = resolveCheckboxIntent(item, myParticipantId, myDisplayName);
        if (intent.kind === "toggle") {
            apply(toggleMyCompletion(item, myParticipantId, myDisplayName, participants));
        }
        else {
            setChoice(intent.variant);
        }
    }, [item, myParticipantId, myDisplayName, participants, apply]);
    // Taking responsibility defaults to covering whatever is still missing.
    const missing = getItemMissingQuantity(item);
    const takeQty = missing > 0 ? missing : 1;
    const sheet = choice ? (_jsx(CompletionChoiceSheet, { variant: choice, onTakeOnly: () => {
            apply(addCurrentParticipantAssignment(item, myParticipantId, myDisplayName, takeQty));
            setChoice(null);
        }, onTakeAndComplete: () => {
            apply(assignAndCompleteForMe(item, myParticipantId, myDisplayName, takeQty));
            setChoice(null);
        }, onClose: () => setChoice(null) })) : null;
    return { onCheckboxTap, sheet };
}
